import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  ArrowLeft,
  Star,
  Zap,
  Crown,
  BookOpen,
  Shield,
  Gift,
  Copy,
  Lock,
} from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { trackFunnel } from "../utils/analytics";
import { useSectionEngagement } from "../hooks/useSectionEngagement";
import { formatApiError } from "../utils/apiError";
import { resolveEffectiveRole } from "../utils/roleResolver";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState({ learner_plans: [], instructor_plans: [], pdi_plans: [] });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [launchLock, setLaunchLock] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);

  const activationSource = searchParams.get("from") || null;
  const fromAssessment = activationSource === "assessment";
  // Effective audience — URL param overrides the user's resolved role
  // so an anonymous visitor coming from `/for-pdis` or an ADI clicking
  // a "See pricing" link never sees learner plans by accident.
  //
  // Membership routing fix (Feb 8, 2026): use the shared roleResolver
  // so admin-previewing-as-instructor (founder mode OR admin_view_role)
  // resolves to "adi"/"pdi", not the old hard-coded "learner" fallback
  // that leaked instructor users into the learner pricing surface.
  const audienceParam = (searchParams.get("audience") || "").toLowerCase();
  const resolvedRole = resolveEffectiveRole(user, { audienceParam });
  // "admin" without a view-role isn't an audience — fall back to learner
  // for the public marketing surface (admin can still see all tabs via
  // their underlying admin tools elsewhere). This preserves the existing
  // public default for unauthenticated visitors and bare admins.
  const effectiveAudience = (resolvedRole === "admin") ? "learner" : resolvedRole;
  const isPdi = effectiveAudience === "pdi";
  const isInstructor = effectiveAudience === "pdi" || effectiveAudience === "adi";
  const displayPlans = isPdi
    ? plans.pdi_plans
    : (isInstructor ? plans.instructor_plans : plans.learner_plans);

  // Section engagement tracking — measure attention + drop-off across pricing sections.
  const [heroRef] = useSectionEngagement("pricing_hero");
  const [foundingRef] = useSectionEngagement("pricing_founding_offer");
  const [plansRef, markPlanCtaClicked] = useSectionEngagement("pricing_plans");
  const [referralRef] = useSectionEngagement("pricing_referral");

  useEffect(() => {
    fetchPlans();
    fetchLaunchLock();
    if (user) {
      fetchReferralInfo();
      if (user.role === "pdi" || user.role === "adi") fetchTrialStatus();
    }
    trackFunnel("subscribe", "pricing_viewed", { role: user?.role || "anon" });
  }, [user]);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/plans`);
      setPlans(data);
    } catch {}
    setLoading(false);
  };

  const fetchLaunchLock = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/launch-lock/status`);
      setLaunchLock(data);
    } catch {}
  };

  const fetchTrialStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/beta/me/trial-status`, { withCredentials: true });
      setTrialStatus(data);
    } catch {}
  };

  const fetchReferralInfo = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/referral/my-info`, { withCredentials: true });
      setReferralInfo(data);
    } catch {}
  };

  const handleSelect = async (planId) => {
    if (!user) {
      navigate(`/register?plan=${planId}`);
      return;
    }
    if (planId === "free") {
      navigate("/dashboard");
      return;
    }
    markPlanCtaClicked({ cta: "select_plan", plan_id: planId });
    trackFunnel("subscribe", "plan_selected", { plan_id: planId, role: user?.role });
    setSubscribing(planId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/subscribe`,
        { origin_url: window.location.origin, plan_id: planId, from_source: activationSource },
        { withCredentials: true }
      );
      if (data.founder_mode || data.no_charge) {
        // Admin/founder Stripe bypass — DO NOT redirect (the previous
        // bug auto-redirected admin to /admin). Show the message
        // returned by the backend so the founder knows what to do next.
        toast.message(data.message || "Admin billing bypass active — use a real test account to test checkout.", { duration: 6000 });
        return;
      }
      if (data.credits_applied) {
        trackFunnel("subscribe", "completed", { plan_id: planId, credits_applied: true });
        toast.success("Subscription activated!");
        await refreshUser();
        navigate("/dashboard");
      } else if (data.url) {
        trackFunnel("subscribe", "checkout_started", { plan_id: planId });
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err, "Unable to start subscription"));
    } finally {
      setSubscribing(null);
    }
  };

  const copyReferral = () => {
    if (!referralInfo) return;
    navigator.clipboard.writeText(`${window.location.origin}/join/${referralInfo.referral_code}`);
    toast.success("Referral link copied!");
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const recommended = displayPlans.find((p) => p.popular) || displayPlans.find((p) => p.id !== "free");

  return (
    <div className="min-h-dvh no-overflow-x bg-white">
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => navigate(user ? "/dashboard" : "/")}
          className="text-slate-400 hover:text-slate-600"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <DarkModeToggle />
      </header>

      <div className="max-w-md mx-auto px-5 pb-12">
        {/* Hero */}
        <div ref={heroRef} data-testid="pricing-hero-section" className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Pick what fits where you are</h1>
          {isPdi ? (
            <p className="text-sm text-slate-500 leading-relaxed">
              The full PDI training engine — Practice Reviews, Mock Part-3, Mastery Builder, and CPD certificates.
            </p>
          ) : isInstructor ? (
            <p className="text-sm text-slate-500 leading-relaxed">
              We handle the admin so you can get on with teaching.
            </p>
          ) : (
            <p className="text-sm text-slate-500">Pass sooner. Spend less on failed tests. Feel ready on the day.</p>
          )}
        </div>

        {/* Credits banner — removed (Feb 6, 2026): referral credits
            UI is part of the legacy growth stack and no longer
            surfaced. Backend logic preserved for any in-flight
            credits already on accounts. */}

        {/* Plans — clean cards only. Feb 6, 2026: removed Launch
            Member Lock / founder-pricing banners / countdown timers
            / scarcity copy per owner directive ("Make the platform
            feel like a professional tool, not a marketing funnel").
            Backend founder-discount logic still exists for legacy
            cohort but is no longer surfaced in the UI. */}
        <div ref={plansRef} data-testid="pricing-plans-section" className="space-y-4 mb-8">
          {displayPlans.map((plan) => {
            const isRec = plan.id === recommended?.id;
            const isCurrent = user?.plan === plan.id;
            const isFree = plan.price === 0;
            const icons = { free: BookOpen, premium: Star, starter: Shield, pro: Zap, elite: Crown, pdi_starter: BookOpen, pdi_plan: Zap, pdi_pro: Crown };
            const Icon = icons[plan.id] || Star;

            return (
              <div
                key={plan.id}
                data-testid={`plan-${plan.id}`}
                className={`relative rounded-2xl border-2 p-5 transition-all ${
                  isRec ? "border-slate-900" : "border-slate-200"
                } ${isCurrent ? "ring-2 ring-emerald-400" : ""}`}
              >
                {isRec && (
                  <span className="absolute -top-3 left-5 bg-slate-900 text-white text-[11px] font-bold px-3 py-1 rounded-full">
                    Best fit for you
                  </span>
                )}
                {isCurrent && (
                  <span className="absolute -top-3 right-5 bg-emerald-500 text-white text-[11px] font-bold px-3 py-1 rounded-full">
                    Current
                  </span>
                )}

                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRec ? "bg-slate-900" : "bg-slate-100"}`}>
                    <Icon className={`w-5 h-5 ${isRec ? "text-white" : "text-slate-600"}`} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{plan.name}</p>
                    {isFree ? (
                      <p className="text-sm text-slate-500">Free forever</p>
                    ) : (
                      <p className="text-sm text-slate-500">£{plan.price.toFixed(2)}/{plan.interval}</p>
                    )}
                  </div>
                </div>
                {plan.tagline && (
                  <p
                    data-testid={`plan-tagline-${plan.id}`}
                    className="text-[13px] text-slate-600 italic leading-snug mb-4 border-l-2 border-slate-200 pl-3"
                  >
                    {plan.tagline}
                  </p>
                )}

                <ul className="space-y-2 mb-4">
                  {((isRec && !isInstructor) ? [
                    "Personalised lesson plan",
                    "Instructor-backed guidance",
                    "Proven first-time pass system",
                  ] : plan.features).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Expandable plan-detail (Feb 6, 2026): "Who it's for /
                    Your tools (grouped) / How this helps". Tap the
                    chevron to open. Premium-style cards do not auto-
                    expand — kept opt-in so the page reads short. */}
                {(plan.who_for || plan.tool_groups?.length || plan.outcome) && (
                  <button
                    data-testid={`plan-toggle-${plan.id}`}
                    onClick={() => setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)}
                    className="w-full mb-3 flex items-center justify-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 py-2"
                  >
                    {expandedPlanId === plan.id ? "Hide details" : "See what's included"}
                    <span className={`transition-transform ${expandedPlanId === plan.id ? "rotate-180" : ""}`}>▾</span>
                  </button>
                )}

                {expandedPlanId === plan.id && (
                  <div data-testid={`plan-detail-${plan.id}`} className="mb-4 space-y-4 border-t border-slate-200 pt-4">
                    {plan.who_for && (
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Who it's for</p>
                        <p className="text-sm text-slate-700 leading-snug">{plan.who_for}</p>
                      </div>
                    )}
                    {plan.tool_groups?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Your tools</p>
                        <div className="space-y-3">
                          {plan.tool_groups.map((g, gi) => (
                            <div key={gi}>
                              <p className="text-[11px] font-extrabold text-slate-900 mb-1">{g.label}</p>
                              <ul className="space-y-1">
                                {(g.items || []).map((item, ii) => (
                                  <li key={ii} className="flex items-start gap-2 text-[13px] text-slate-700">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {plan.outcome && (
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">How this helps</p>
                        <p className="text-sm text-slate-700 leading-snug italic">{plan.outcome}</p>
                      </div>
                    )}
                  </div>
                )}
                {isRec && !isInstructor && (
                  <p className="text-xs text-slate-500 mb-5">Where most learners start.</p>
                )}
                {isRec && isInstructor && (
                  <p className="text-xs text-slate-500 mb-5">Where most instructors start.</p>
                )}

                <Button
                  data-testid={`select-${plan.id}`}
                  onClick={() => handleSelect(plan.id)}
                  disabled={!!subscribing || isCurrent}
                  className={`w-full h-12 rounded-xl font-bold text-sm ${
                    isCurrent
                      ? "bg-emerald-100 text-emerald-700 cursor-default"
                      : isRec
                      ? "bg-slate-900 hover:bg-slate-800 text-white"
                      : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {isCurrent
                    ? "You're on this one"
                    : subscribing === plan.id
                    ? "Hold on..."
                    : isRec
                    ? "Continue"
                    : isFree
                    ? "Stay free for now"
                    : "Pick this one"}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Referral */}
        {user && referralInfo && (
          <div ref={referralRef} data-testid="referral-section" className="bg-slate-50 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-5 h-5 text-slate-600" />
              <div>
                <p className="font-bold text-slate-900 text-sm">Refer & Earn</p>
                <p className="text-xs text-slate-500">
                  {user.role === "learner" ? "£5 credit per friend" : "1 month free per instructor"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="referral-code" className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2.5 font-mono font-bold text-slate-900 text-center text-sm tracking-widest">
                {referralInfo.referral_code}
              </code>
              <Button data-testid="copy-referral-btn" onClick={copyReferral} variant="outline" size="sm" className="h-10 px-3 rounded-lg">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Not logged in */}
        {!user && (
          <p className="text-center text-sm text-slate-500 mt-6">
            Already with us?{" "}
            <button onClick={() => navigate("/login")} className="text-blue-600 font-medium hover:underline">Sign in</button>
          </p>
        )}
      </div>
    </div>
  );
}
